import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, map, startWith } from 'rxjs';
import { Address } from 'src/app/models/address.model';
import { BusinessInformationModel } from 'src/app/models/business-information.model';
import { LookupObjectModel } from 'src/app/models/lookup-object.model';
import { TPApplicationInfoModel } from 'src/app/models/training-provider-application.model';
import { LookupObjectService } from 'src/app/services/lookup-object/lookup-object.service';
import { phoneValidatorPattern } from 'src/app/utils/validators.util';
import { PredictiveAddressSearchComponent } from '../predictive-address-search/predictive-address-search.component';

@Component({
  selector: 'app-business-information',
  templateUrl: './business-information.component.html',
  styleUrls: ['./business-information.component.scss'],
  providers: [],
  standalone: false
})
export class BusinessInformationComponent implements OnInit {
  @Input() showSameMailing: boolean = true
  @Input() showErrorsOverride: boolean = false
  @Input() editApprovedMode: boolean = false
  @Input() soseMode: boolean = false
  @ViewChild("busAddress") busAddress: PredictiveAddressSearchComponent;

  currentLang: string = 'en';
  @Input() businessInformation: BusinessInformationModel;
  businessInfoForm: FormGroup;
  correspondencePreferenceOptions: LookupObjectModel[] = [];
  isValidBN9: boolean | undefined
  bn9CallInProgress: boolean = false

  @Output() sameAddressOptionChanged = new EventEmitter<boolean>();
  isReadOnlyMode = false;

  constructor(
    public translateService: TranslateService,
    private formBuilder: FormBuilder,
    @Inject('LookupObjectService') public lookupObjectService: LookupObjectService) {
    this.currentLang = translateService.currentLang;
    translateService.onLangChange.subscribe(lang => {
      this.currentLang = lang.lang;
    });
  }

  ngOnInit(): void {
    this.initData();
    this.buildForm()
    this.populateData()
  }

  initData(): void {
    if (!this.businessInformation) {
      const bim = new BusinessInformationModel();
      bim.OperatingName = '';
      bim.LegalName = '';
      bim.BusinessType = '';
      bim.BusinessNumber = '';
      bim.WSIBAccountNumber = '';
      bim.PrimaryAddress = new Address('00000000-0000-0000-0000-000000000000', '', '', '', '', '', '', 'Canada');
      this.businessInformation = bim;
    }
  }

  setBusinessInfo(bim: BusinessInformationModel): void {
    this.businessInformation = bim;
    this.buildForm();
    if (!bim.CorrespondenceOption) this.populateData()
    this.busAddress.setAddress(bim.PrimaryAddress);
  }

  populateData() {
    this.lookupObjectService.getLookupObjectsByGroupName("CorrespondencePreference")
      .subscribe((data) => {
        this.correspondencePreferenceOptions = data;
        if (data.length > 0) {
          this.businessInfoForm.controls["CorrespondenceOption"].setValue(data[0].LookupObjectId + ",");
        }
        this.bn9Validation();
      });
  }

  buildForm() {
    this.businessInfoForm = this.formBuilder.group({
      OperatingName: new FormControl(this.businessInformation.OperatingName, [Validators.maxLength(256), Validators.required]),
      LegalName: new FormControl(this.businessInformation.LegalName, [Validators.maxLength(256), Validators.required]),
      businessNumber: new FormControl(this.businessInformation.BusinessNumber, this.soseMode ? [Validators.maxLength(9), Validators.pattern('^\\d{9}$'), Validators.required] : [Validators.maxLength(9), Validators.pattern('^\\d{9}$')]),
      businessType: new FormControl(this.businessInformation.BusinessType, this.soseMode ? [] : [Validators.maxLength(256), Validators.required]),
      wsibAccountNumber: new FormControl(this.businessInformation.WSIBAccountNumber, this.soseMode ? [Validators.required, Validators.maxLength(7), Validators.pattern('^\\d{7}$')] : [Validators.maxLength(7), Validators.pattern('^\\d{7}$')]),
      AddressForm: this.formBuilder.group({
        address: new FormControl(this.businessInformation.PrimaryAddress.AddressLine, [Validators.maxLength(500), Validators.required]),
        aptSuitUnit: new FormControl(this.businessInformation.PrimaryAddress.AptSuiteUnit, [Validators.maxLength(500)]),
        city: new FormControl(this.businessInformation.PrimaryAddress.CityTown, [Validators.required, Validators.maxLength(100)]),
        province: new FormControl(this.businessInformation.PrimaryAddress.Province, [Validators.required]),
        postalCode: new FormControl(this.businessInformation.PrimaryAddress.PostalCode, [
          Validators.required, Validators.maxLength(7)]),
      }),
      country: new FormControl('Canada', [Validators.required]),
      poBox: new FormControl(this.businessInformation.PrimaryAddress.PoBox, []),
      companyWebSite: new FormControl(this.businessInformation.CompanyWebSite, [Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')]),
      CorrespondenceOption: new FormControl(this.businessInformation.CorrespondenceOption, [Validators.required]),
      isMailingAndBusinessAddressSame: new FormControl(false, [])
    });
    if (this.soseMode) {
      this.businessInfoForm.addControl('OrganizationPhoneNumber', new FormControl(this.businessInformation.PhoneNumber.split('x')[0], [Validators.pattern(phoneValidatorPattern), Validators.required]));
      this.businessInfoForm.addControl('Extension', new FormControl(this.businessInformation.PhoneNumber.split('x')[1]));
    }
  }

  resetBusinessInfoForm() {
    this.busAddress.clearFormFields(this.businessInfoForm.get('AddressForm.address')?.value);
    this.businessInfoForm.get('AddressForm.address')?.reset();
    this.setBusinessAddressSameAsMailing(this.businessInfoForm.get('AddressForm')?.value, false);
    this.buildForm();

  }

  bn9Validation(): void {
    this.businessInfoForm.controls['businessNumber'].valueChanges.pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value : value.name)),
      debounceTime(200)
    ).subscribe(busNum => {
      this.bn9CallInProgress = false
      if (busNum != null && busNum.length == 9) {
        this.isValidBN9 = true;
      }
    });
  }

  setCorrespondenceOption(lookupObjectId: string) {
    let stringVal: string = this.businessInfoForm.controls["CorrespondenceOption"].value
    if (stringVal.indexOf(lookupObjectId) > -1) {
      stringVal = stringVal.replace(lookupObjectId + ',', '')
      this.businessInfoForm.controls["CorrespondenceOption"].setValue(stringVal)
      this.businessInfoForm.controls["CorrespondenceOption"].markAsTouched();
    }
    else this.businessInfoForm.controls["CorrespondenceOption"].setValue(stringVal + lookupObjectId + ',');
  }

  saveBusinessInformation() {
    let corVal = this.businessInfoForm.controls['CorrespondenceOption'].value
    if (corVal.length > 0 && corVal[corVal.length - 1] == ',') corVal = corVal.substring(0, corVal.length - 1)

    this.businessInformation =
    {
      OperatingName: this.businessInfoForm.controls['OperatingName'].value,
      LegalName: this.businessInfoForm.controls['LegalName'].value,
      BusinessNumber: this.businessInfoForm.controls['businessNumber'].value,
      BusinessType: this.businessInfoForm.controls['businessType'].value,
      WSIBAccountNumber: this.businessInfoForm.controls['wsibAccountNumber'].value,
      PrimaryAddress: this.getAddress(),
      CompanyWebSite: this.businessInfoForm.controls['companyWebSite'].value,
      CorrespondenceOption: corVal,
      CorrespondenceOptionValue: this.getCorrespondencePreferenceById(corVal),
      isMailingAndBusinessAddressSame: this.businessInfoForm.controls['isMailingAndBusinessAddressSame'].value,
      isValidBN9: this.isValidBN9,
      PhoneNumber: this.soseMode ? this.businessInfoForm.controls['OrganizationPhoneNumber'].value + 'x' + (this.businessInfoForm.controls['Extension'].value ?? '') : ''
    };
    return this.businessInformation
  }

  private getAddress(): Address {
    if (this.busAddress?.AddressForm) {
      return new Address('00000000-0000-0000-0000-000000000000'
        , this.busAddress.AddressForm.get('postalCode')!.value
        , this.busAddress.AddressForm.get('address')!.value
        , this.busAddress.AddressForm.get('aptSuitUnit')!.value
        , this.businessInfoForm.controls['poBox'].value
        , this.busAddress.AddressForm.get('city')!.value
        , this.busAddress.AddressForm.get('province')!.value
        , this.businessInfoForm.controls['country'].value);
    } else {
      return new Address('00000000-0000-0000-0000-000000000000'
        , this.businessInfoForm.get('AddressForm.postalCode')!.value
        , this.businessInfoForm.get('AddressForm.address')!.value
        , this.businessInfoForm.get('AddressForm.aptSuitUnit')!.value
        , this.businessInfoForm.controls['poBox'].value
        , this.businessInfoForm.get('AddressForm.city')!.value
        , this.businessInfoForm.get('AddressForm.province')!.value
        , this.businessInfoForm.controls['country'].value);
    }
  }

  getPrimaryAddress(): Address {
    return this.businessInformation.PrimaryAddress;
  }

  valid(): boolean {
    let isFormValid = true;
    Object.keys(this.businessInfoForm.controls).forEach((control: string) => {
      const typedControl: AbstractControl = this.businessInfoForm.controls[control];
      if (!typedControl.valid && control != 'AddressForm') {
        isFormValid = false;
      }
      // should log the form controls value and be typed correctly
    });
    return isFormValid && this.busAddress.valid();
  }


  getCorrespondencePreferenceById(id: string) {
    if (id == null || id == '') return id
    let option = this.correspondencePreferenceOptions.filter(x => x.LookupObjectId == id.replace(',', '').toLowerCase())[0]
    if (option == null || option == undefined) return null

    return this.currentLang == 'en' ? option.Value : option.ValueFr
  }

  setBusinessAddressSameAsMailing(mailingAddr: TPApplicationInfoModel, on: boolean) {
    if (on) {
      this.busAddress.setAddress(mailingAddr.MailingAddress!);
      this.busAddress.setDisabledMode(true);
      this.businessInfoForm.controls['poBox'].setValue(mailingAddr.MailingAddress!.PoBox);
      this.isReadOnlyMode = true;
    } else {
      this.busAddress.setDisabledMode(false);
      this.isReadOnlyMode = false;
    }
  }

  updateAddress($event: Address) {
    if (this.businessInfoForm.get("isMailingAndBusinessAddressSame")!.value) {
      const mailingAddr = new TPApplicationInfoModel();
      mailingAddr.MailingAddress = $event;
      this.setBusinessAddressSameAsMailing(mailingAddr, true);
    }
  }
}

